/* eslint-disable indent */
import { useEffect, useRef, useState, useCallback } from 'react';
import { TResultImageCoordinatesSchema } from '@/schemas/vehicule-check-result/result-image-coordinates.schema';
import { twMerge } from 'tailwind-merge';
import { Spinner } from '@/components/Loading/Spinner';
import {
  useBoxCoordinates,
  usePolygonPoints,
  useDenormalizePolygonPoints,
  usePolygonScalePoints,
} from '@/hooks/moderation.hooks';
import { ImageDimension } from '@/type/detection.types';

type ImgWithYoloBoxProps = {
  imgSrc: string;
  imageClassName?: string;
  rectangleClassName?: string;
  polygonClassName?: string;
  polygonThickness?: number;
  coordinates?: TResultImageCoordinatesSchema;
  originalBoundingBox?: Array<number>;
  manyRectangleAnnotations?: TResultImageCoordinatesSchema[];
  manyPolygonAnnotations?: TResultImageCoordinatesSchema[];
  disableLazyLoading?: boolean;
  onImageLoading?: (loading: boolean) => void;
  onClick?: () => void;
};

export const ImgWithYoloBox = ({
  imgSrc,
  imageClassName,
  rectangleClassName,
  polygonClassName,
  polygonThickness = 5, // 2.5 on modal preview
  coordinates,
  originalBoundingBox,
  manyRectangleAnnotations,
  manyPolygonAnnotations,
  disableLazyLoading = false,
  onImageLoading,
  onClick,
}: ImgWithYoloBoxProps) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const manyPolygonsCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);
  const [polygonImageUrl, setPolygonImageUrl] = useState<string | null>(null);
  const [manyPolygonsImageUrl, setManyPolygonsImageUrl] = useState<
    string | null
  >(null);
  const [isImageLoading, setIsImageLoading] = useState(true);
  const [imageDimension, setImageDimension] = useState<ImageDimension | null>(
    null,
  );

  const isModerated = coordinates?.moderated;

  const { getCoordinates: getBoxCoordinates } = useBoxCoordinates({
    isModerated,
    coordinates,
    imageDimension,
  });

  const box = getBoxCoordinates();

  const { getPoints: polygonPoints } = usePolygonPoints({ coordinates });

  const { getDenormalizedCoordinates } = useDenormalizePolygonPoints({
    originalBoundingBox,
    coordinates,
    imageDimension,
  });

  const { getScalePoints: getPolygonScalePoints } = usePolygonScalePoints({
    isModerated,
    polygonPoints,
    denormalizePolygonPoints: getDenormalizedCoordinates,
  });

  useEffect(() => {
    onImageLoading?.(isImageLoading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isImageLoading]);

  // Callback ref to handle image loading for both conditional image elements
  const setImgRef = useCallback(
    (img: HTMLImageElement | null) => {
      if (!img) return;

      // Store the img reference
      imageRef.current = img;

      // Set loading to true when imgSrc changes
      setIsImageLoading(true);

      const handleLoaded = () => {
        // Set loading to false when image has loaded
        setIsImageLoading(false);
        setImageDimension({
          width: img.naturalWidth,
          height: img.naturalHeight,
        });
      };

      img.onload = handleLoaded;

      // Handle case where image is already cached
      if (img.complete) {
        handleLoaded();
      }
    },
    [imgSrc],
  );

  // Create a canvas with the polygon and generate a data URL
  useEffect(() => {
    if (!polygonPoints || polygonPoints.length < 3) {
      setPolygonImageUrl(null);
      return;
    }

    // Create a canvas element if it doesn't exist
    if (!canvasRef.current) {
      canvasRef.current = document.createElement('canvas');
    }

    const canvas = canvasRef.current;

    // Use a much larger canvas for better quality
    canvas.width = 1000;
    canvas.height = 1000;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Create a completely transparent base
    ctx.fillStyle = 'rgba(255, 255, 255, 0)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    const scaledPoints = getPolygonScalePoints(canvas);
    if (!scaledPoints?.length) return;

    // First: Draw the blue fill
    ctx.beginPath();
    ctx.moveTo(scaledPoints[0].x, scaledPoints[0].y);

    for (let i = 1; i < scaledPoints.length; i++) {
      ctx.lineTo(scaledPoints[i].x, scaledPoints[i].y);
    }

    // Close the path
    ctx.closePath();

    // Fill with semi-transparent blue
    ctx.fillStyle = 'rgba(0, 0, 255, 0.1)';
    ctx.fill();

    // Second: Draw cleaner border lines
    // Anti-aliasing for smooth lines
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Draw red border
    ctx.beginPath();
    ctx.moveTo(scaledPoints[0].x, scaledPoints[0].y);

    for (let i = 1; i < scaledPoints.length; i++) {
      ctx.lineTo(scaledPoints[i].x, scaledPoints[i].y);
    }

    // Close the polygon path
    ctx.lineTo(scaledPoints[0].x, scaledPoints[0].y);

    // Set line properties for clean rendering
    ctx.lineWidth = polygonThickness;
    ctx.strokeStyle = 'red';
    ctx.lineJoin = 'round';
    ctx.lineCap = 'round';
    ctx.stroke();

    // Generate data URL with PNG format for best quality
    try {
      const dataUrl = canvas.toDataURL('image/png', 1.0); // Maximum quality
      setPolygonImageUrl(dataUrl);
    } catch (error) {
      console.error('Error generating polygon image:', error);
      setPolygonImageUrl(null);
    }
  }, [polygonPoints, imageDimension, polygonThickness, getPolygonScalePoints]);

  // Create a canvas with multiple polygons and generate a data URL
  useEffect(() => {
    if (!manyPolygonAnnotations || manyPolygonAnnotations.length === 0) {
      setManyPolygonsImageUrl(null);
      return;
    }

    // Create a canvas element if it doesn't exist
    if (!manyPolygonsCanvasRef.current) {
      manyPolygonsCanvasRef.current = document.createElement('canvas');
    }

    const canvas = manyPolygonsCanvasRef.current;

    // Use a much larger canvas for better quality
    canvas.width = 1000;
    canvas.height = 1000;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Create a completely transparent base
    ctx.fillStyle = 'rgba(255, 255, 255, 0)';
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    // Anti-aliasing for smooth lines
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    // Draw each polygon
    manyPolygonAnnotations.forEach((annotation) => {
      const points = annotation?.point;
      if (!points || points.length < 3) return;

      // Scale points to canvas dimensions (points are normalized 0-1)
      const scaledPoints = points.map((p) => ({
        x: (p.x || 0) * canvas.width,
        y: (p.y || 0) * canvas.height,
      }));

      // Draw the fill
      ctx.beginPath();
      ctx.moveTo(scaledPoints[0].x, scaledPoints[0].y);

      for (let i = 1; i < scaledPoints.length; i++) {
        ctx.lineTo(scaledPoints[i].x, scaledPoints[i].y);
      }

      ctx.closePath();

      // Fill with semi-transparent red
      ctx.fillStyle = 'rgba(255, 0, 0, 0.1)';
      ctx.fill();

      // Draw border
      ctx.beginPath();
      ctx.moveTo(scaledPoints[0].x, scaledPoints[0].y);

      for (let i = 1; i < scaledPoints.length; i++) {
        ctx.lineTo(scaledPoints[i].x, scaledPoints[i].y);
      }

      ctx.lineTo(scaledPoints[0].x, scaledPoints[0].y);

      // Set line properties for clean rendering
      ctx.lineWidth = polygonThickness;
      ctx.strokeStyle = 'red';
      ctx.lineJoin = 'round';
      ctx.lineCap = 'round';
      ctx.stroke();
    });

    // Generate data URL with PNG format for best quality
    try {
      const dataUrl = canvas.toDataURL('image/png', 1.0);
      setManyPolygonsImageUrl(dataUrl);
    } catch (error) {
      console.error('Error generating many polygons image:', error);
      setManyPolygonsImageUrl(null);
    }
  }, [manyPolygonAnnotations, polygonThickness]);

  // Convert normalized center-based coordinates to percentage-based top-left positioning
  const convertToBoxStyle = (modElement: TResultImageCoordinatesSchema) => {
    const left = (modElement.xCenter - modElement.width / 2) * 100;
    const top = (modElement.yCenter - modElement.height / 2) * 100;
    const width = modElement.width * 100;
    const height = modElement.height * 100;

    return {
      top: `${top}%`,
      left: `${left}%`,
      width: `${width}%`,
      height: `${height}%`,
    };
  };

  return (
    <div
      className="flex items-center justify-center h-full w-full"
      onClick={onClick}
    >
      <div className="relative inline-block text-[0px]">
        {/* If contains polygon points, we'll show the polygon */}
        {polygonPoints?.length > 0 ? (
          <div className="relative">
            {/* Base image */}
            <img
              ref={setImgRef}
              src={imgSrc}
              alt="img"
              loading={disableLazyLoading ? 'eager' : 'lazy'}
              decoding={disableLazyLoading ? 'sync' : 'async'}
              className={twMerge(
                'h-full w-full object-contain object-center',
                imageClassName,
                isImageLoading && 'opacity-0',
              )}
            />
            {/* Overlay the pre-rendered polygon image */}
            {polygonImageUrl && (
              <img
                src={polygonImageUrl}
                alt=""
                className={twMerge(
                  'absolute top-0 left-0 w-full h-full object-fill pointer-events-none',
                  polygonClassName,
                  isImageLoading && 'opacity-0',
                )}
              />
            )}
            {/* Render element annotation rectangles */}
            {manyRectangleAnnotations?.map((modElement, index) => (
              <div
                key={`mod-${modElement.elNumber}-${index}`}
                className={twMerge(
                  'absolute border-[2.5px] border-red-FF0000 rounded-sm pointer-events-none',
                  rectangleClassName,
                  isImageLoading && 'opacity-0',
                )}
                style={convertToBoxStyle(modElement)}
              />
            ))}
            {/* Overlay the pre-rendered many polygons image */}
            {manyPolygonsImageUrl && (
              <img
                src={manyPolygonsImageUrl}
                alt=""
                className={twMerge(
                  'absolute top-0 left-0 w-full h-full object-fill pointer-events-none',
                  polygonClassName,
                  isImageLoading && 'opacity-0',
                )}
              />
            )}
          </div>
        ) : (
          <>
            <img
              ref={setImgRef}
              src={imgSrc}
              alt="img"
              loading={disableLazyLoading ? 'eager' : 'lazy'}
              decoding={disableLazyLoading ? 'sync' : 'async'}
              className={twMerge(
                'h-full w-full object-contain object-center',
                imageClassName,
                isImageLoading && 'opacity-0',
              )}
            />
            {box?.x != null &&
              box?.y != null &&
              box?.width != null &&
              box?.height != null && (
                <div
                  className={twMerge(
                    'absolute border-[2.5px] border-red-FF0000 rounded-sm pointer-events-none',
                    rectangleClassName,
                    isImageLoading && 'opacity-0',
                  )}
                  style={{
                    top: `${box?.y}%`,
                    left: `${box?.x}%`,
                    width: `${box?.width}%`,
                    height: `${box?.height}%`,
                  }}
                />
              )}
            {/* Render element annotation rectangles */}
            {manyRectangleAnnotations?.map((modElement, index) => (
              <div
                key={`mod-${modElement.elNumber}-${index}`}
                className={twMerge(
                  'absolute border-[2.5px] border-red-FF0000 rounded-sm pointer-events-none',
                  rectangleClassName,
                  isImageLoading && 'opacity-0',
                )}
                style={convertToBoxStyle(modElement)}
              />
            ))}
            {/* Overlay the pre-rendered many polygons image */}
            {manyPolygonsImageUrl && (
              <img
                src={manyPolygonsImageUrl}
                alt=""
                className={twMerge(
                  'absolute top-0 left-0 w-full h-full object-fill pointer-events-none',
                  polygonClassName,
                  isImageLoading && 'opacity-0',
                )}
              />
            )}
          </>
        )}

        {isImageLoading && <Spinner />}
      </div>
    </div>
  );
};
